/**
 * System keys for Application Permissions
 * Matches backend Permissions constants
 */
export const PERMISSIONS = {
  // Client permissions
  ClientsView: "clients.view",
  ClientsCreate: "clients.create",
  ClientsUpdate: "clients.update",
  ClientsDelete: "clients.delete",
  ClientsActivate: "clients.activate",

  // Client User permissions
  ClientUsersView: "client_users.view",
  ClientUsersCreate: "client_users.create",
  ClientUsersUpdate: "client_users.update",

  // Client Grant permissions
  ClientGrantsManage: "client_grants.manage",

  // Contract permissions
  ContractsView: "contracts.view",
  ContractsCreate: "contracts.create",
  ContractsUpdate: "contracts.update",
  ContractsDelete: "contracts.delete",

  // Site permissions
  SitesView: "sites.view",
  SitesCreate: "sites.create",
  SitesUpdate: "sites.update",
  SitesDelete: "sites.delete",

  // Employee permissions
  EmployeesView: "employees.view",
  EmployeesCreate: "employees.create",
  EmployeesUpdate: "employees.update",
  EmployeesPromote: "employees.promote",
  EmployeesSuspend: "employees.suspend",
  EmployeesEnd: "employees.end",

  // Guard permissions
  GuardsView: "guards.view",
  GuardsCreate: "guards.create",
  GuardsUpdate: "guards.update",

  // Access Profile permissions
  AccessProfilesView: "access_profiles.view",
  AccessProfilesCreate: "access_profiles.create",
  AccessProfilesUpdate: "access_profiles.update",
  AccessProfilesDelete: "access_profiles.delete",
  AccessProfilesAssign: "access_profiles.assign",

  // User Profile permissions (personal data)
  UserProfilesUpdate: "user_profiles.update",

  // User permissions
  UsersView: "users.view",
  UsersCreate: "users.create",
  UsersUpdate: "users.update",
  UsersToggleStatus: "users.toggle_status",

  // Account permissions
  AccountView: "account.view",
  AccountUpdate: "account.update",

  // Account Configuration permissions
  AccountConfigView: "account_config.view",
  AccountConfigCreate: "account_config.create",
  AccountConfigUpdate: "account_config.update",
  AccountConfigDelete: "account_config.delete",

  // Scheduler Template permissions
  SchedulerTemplatesView: "scheduler_templates.view",
  SchedulerTemplatesCreate: "scheduler_templates.create",
  SchedulerTemplatesUpdate: "scheduler_templates.update",
  SchedulerTemplatesDelete: "scheduler_templates.delete",

  // Scheduler Shift permissions
  SchedulerShiftsView: "scheduler_shifts.view",
  SchedulerShiftsCreate: "scheduler_shifts.create",
  SchedulerShiftsUpdate: "scheduler_shifts.update",
  SchedulerShiftsDelete: "scheduler_shifts.delete",
  SchedulerShiftsPublish: "scheduler_shifts.publish",

  // Scheduler Notes permissions
  SchedulerNotesView: "scheduler_notes.view",
  SchedulerNotesCreate: "scheduler_notes.create",
  SchedulerNotesUpdate: "scheduler_notes.update",
  SchedulerNotesDelete: "scheduler_notes.delete",

  // Scheduler Attendance permissions
  SchedulerAttendanceView: "scheduler_attendance.view",
  SchedulerAttendanceCreate: "scheduler_attendance.create",
  SchedulerAttendanceUpdate: "scheduler_attendance.update",

  // Guard Time Off permissions
  GuardTimeOffView: "guard_time_off.view",
  GuardTimeOffCreate: "guard_time_off.create",
  GuardTimeOffUpdate: "guard_time_off.update",
  GuardTimeOffDelete: "guard_time_off.delete",

  // Patrol permissions
  PatrolView: "patrol.view",
  PatrolCreate: "patrol.create",
  PatrolUpdate: "patrol.update",
  PatrolDelete: "patrol.delete",

  // Guard Location permissions
  GuardLocationsView: "guard_locations.view",

  // Incident permissions
  IncidentsView: "incidents.view",
  IncidentsCreate: "incidents.create",
  IncidentsReview: "incidents.review",
  IncidentsConfigure: "incidents.configure",

  // Messaging permissions
  MessagingOps: "messaging.ops",
  MessagingChat: "messaging.chat",
  MessagingBroadcast: "messaging.broadcast",

  // Memo Book permissions
  MemoBookView: "memo_book.view",

  OfficeTimeManage: "office_time.manage",

  // Payroll permissions
  PayrollView: "payroll.view",
  PayrollGenerate: "payroll.generate",
  PayrollAdjust: "payroll.adjust",
  PayrollPublish: "payroll.publish",
  PayrollRates: "payroll.rates",

  // Finance permissions
  FinanceView: "finance.view",
  FinanceManage: "finance.manage",

  // Employee Configuration permissions
  EmployeeConfigurationManage: "employee_configuration.manage",

  // Scheduler Settings permissions
  SchedulerSettingsManage: "scheduler_settings.manage",

  // Daily activity report permissions
  DailyReportsView: "daily_reports.view",
  DailyReportsPublish: "daily_reports.publish",

  // Coverage Request permissions
  CoverageRequestsView: "coverage_requests.view",
  CoverageRequestsDecide: "coverage_requests.decide",

  // Audit permissions
  AuditView: "audit.view",

  // Billing permissions (both survive a past-due or suspended account)
  BillingView: "billing.view",
  BillingManage: "billing.manage",

  // Requesting a downloadable copy of everything the account holds. Its own permission because it is
  // a much larger right than reading or paying the bill: whoever holds it can take the roster, the
  // schedule, attendance, incidents, payroll, clients and sites out of the building in one file.
  // It survives the read-only wind-down window and is withdrawn while an account is past due or
  // suspended.
  AccountExport: "account.export",
} as const;

export type PermissionKey = keyof typeof PERMISSIONS;
export type PermissionString = typeof PERMISSIONS[PermissionKey];
